import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useAcademicRenewalFormTexts } from './hooks/useAcademicRenewalFormTexts';
import MultiSelectDropdown from './MultiSelectDropdown';
import './AcademicRenewalForm.css';

interface AcademicRenewalFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: RenewalFormData) => Promise<void>;
  loading: boolean;
}

export interface RenewalFormData {
  mainReason: string;
  usageFrequency: string[];
  usefulness: number;
  suggestions: string;
  access: string[];
  applicationTypeOther?: string;
  /** Required only when suggestions is non-empty */
  marketingConsent?: 'yes' | 'no';
}

const AcademicRenewalForm: React.FC<AcademicRenewalFormProps> = ({ open, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState<RenewalFormData>({
    mainReason: '',
    usageFrequency: [],
    usefulness: 3,
    suggestions: '',
    access: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const t = useAcademicRenewalFormTexts();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.mainReason) {
      newErrors.mainReason = t.errorMainReason;
    }

    if (!formData.usageFrequency?.length) {
      newErrors.usageFrequency = t.errorUsageFrequency;
    }

    if (!formData.access?.length) {
      newErrors.access = t.errorAccess;
    }

    if (formData.usageFrequency?.includes('other') && !formData.applicationTypeOther?.trim()) {
      newErrors.applicationTypeOther = t.errorApplicationTypeOther;
    }

    if (formData.usefulness < 1 || formData.usefulness > 5) {
      newErrors.usefulness = t.errorUsefulness;
    }

    const hasSuggestions = !!formData.suggestions?.trim();
    if (hasSuggestions && formData.marketingConsent !== 'yes' && formData.marketingConsent !== 'no') {
      newErrors.marketingConsent = t.errorMarketingConsent;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting renewal form:', error);
    }
  };

  const handleInputChange = (field: keyof RenewalFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const setMultiSelect = (field: 'usageFrequency' | 'access', value: string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'usageFrequency' && errors.applicationTypeOther) {
      setErrors(prev => ({ ...prev, applicationTypeOther: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-6 pt-6 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-8">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 inline-flex justify-center rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-pink-100 to-purple-100 mb-4">
                <DocumentTextIcon className="w-8 h-8 text-pink-600" />
              </div>
              <DialogTitle as="h3" className="text-xl font-bold leading-6 text-gray-900">
                {t.title}
              </DialogTitle>
              <p className="mt-2 text-sm text-gray-600">{t.description}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question 1: Main Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t.questionMainReason} <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {t.mainReasonOptions.map(option => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="mainReason"
                        value={option.value}
                        checked={formData.mainReason === option.value}
                        onChange={e => handleInputChange('mainReason', e.target.value)}
                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.mainReason && <p className="mt-1 text-sm text-red-600">{errors.mainReason}</p>}
              </div>

              {/* Question 2: Application type (dropdown multiselect) */}
              <div>
                <MultiSelectDropdown
                  label={t.questionUsageFrequency}
                  options={t.usageFrequencyOptions}
                  value={formData.usageFrequency ?? []}
                  onChange={v => setMultiSelect('usageFrequency', v)}
                  placeholder={t.selectFrequencyPlaceholder}
                  theme="pink"
                  error={errors.usageFrequency}
                  required
                />
                {formData.usageFrequency?.includes('other') && (
                  <input
                    type="text"
                    value={formData.applicationTypeOther ?? ''}
                    onChange={e => handleInputChange('applicationTypeOther', e.target.value)}
                    placeholder={t.otherSpecifyPlaceholder}
                    className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                  />
                )}
                {errors.applicationTypeOther && (
                  <p className="mt-1 text-sm text-red-600">{errors.applicationTypeOther}</p>
                )}
              </div>

              {/* Question 3: Access (dropdown multiselect) */}
              <div>
                <MultiSelectDropdown
                  label={t.questionAccess}
                  options={t.accessOptions}
                  value={formData.access ?? []}
                  onChange={v => setMultiSelect('access', v)}
                  placeholder={t.selectAccessPlaceholder}
                  theme="pink"
                  error={errors.access}
                  required
                />
              </div>

              {/* Question 3: Usefulness Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t.questionUsefulness} <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.usefulness}
                    onChange={e => handleInputChange('usefulness', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-medium text-gray-700">
                      {t.usefulnessLabels[formData.usefulness - 1]}
                    </span>
                  </div>
                </div>
                {errors.usefulness && <p className="mt-1 text-sm text-red-600">{errors.usefulness}</p>}
              </div>

              {/* Question 4: Suggestions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t.questionSuggestions}
                </label>
                <textarea
                  value={formData.suggestions}
                  onChange={e => handleInputChange('suggestions', e.target.value)}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                  placeholder={t.suggestionsPlaceholder}
                />
              </div>

              {/* Question 5 (conditional): Marketing consent - only when user wrote suggestions */}
              {formData.suggestions?.trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t.questionMarketingConsent} <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="marketingConsent"
                        value="yes"
                        checked={formData.marketingConsent === 'yes'}
                        onChange={() => handleInputChange('marketingConsent', 'yes')}
                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t.marketingConsentYes}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="marketingConsent"
                        value="no"
                        checked={formData.marketingConsent === 'no'}
                        onChange={() => handleInputChange('marketingConsent', 'no')}
                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t.marketingConsentNo}</span>
                    </label>
                  </div>
                  {errors.marketingConsent && (
                    <p className="mt-1 text-sm text-red-600">{errors.marketingConsent}</p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                  disabled={loading}
                >
                  {t.btnCancel}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-2 text-sm font-medium text-white border border-transparent rounded-xl shadow-md transition-all duration-200 ${
                    loading
                      ? 'btn-gradient-color opacity-50 cursor-not-allowed'
                      : 'btn-gradient-color hover:from-pink-600 hover:to-purple-800 hover:scale-105'
                  }`}
                >
                  {loading ? t.btnSubmitting : t.btnSubmit}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default AcademicRenewalForm;
